import { responseStatus } from '../util/Fetch';
import CompanyDetails from '../components/CompanyDetails';

export default class CompaniesList {
  /**
   * Companies list
   *
   * @param {HTMLElement} companiesElt  The companies wrapper element
   * @param {HTMLElement} listElt  The companies list element
   * @param {HTMLElement} detailsElt  The company details element
   * @param {HTMLElement} navElt  The company nav element
   */
  constructor(companiesElt, listElt, detailsElt, navElt) {
    /**
     * @property {HTMLElement} companiesElt  The companies wrapper element
     */
    this.companiesElt = companiesElt;

    /**
     * @property {HTMLElement} listElt  The companies list element
     */
    this.listElt = listElt;

    /**
     * @property {HTMLElement} detailsElt  The company details element
     */
    this.detailsElt = detailsElt;

    /**
     * @property {HTMLElement} navElt  The company nav element
     */
    this.navElt = navElt;

    /**
     * @property {HTMLElement} navElt  The company nav element
     */
    this.titleElt = this.companiesElt.querySelector('.companies-title');

    /**
     * @property {HTMLElement[]} companiesArticlesElts  The companies articles elements
     */
    this.companiesArticlesElts = this.listElt.querySelectorAll('.companies-article');

    /**
     * @property {HTMLElement} prevButton  The company nav previous button element
     */
    this.prevButtonElt = navElt.querySelector('.companies-nav-prev');

    /**
     * @property {HTMLElement} nextButton  The company nav next button element
     */
    this.nextButtonElt = navElt.querySelector('.companies-nav-next');

    /**
     * @property {string} restUrl  The WP rest url for the companies
     */
    this.restUrl = this.listElt.getAttribute('data-rest-url');

    /**
     * @property {string} restCategoryUrl  The WP rest url for the categories
     */
    this.restCategoryUrl = this.listElt.getAttribute('data-rest-category-url');

    /**
     * @property {number} perPage  The number of companies to display per page
     */
    this.perPage = parseInt(listElt.getAttribute('data-per-page'), 10);

    /**
     * @property {number} currentPage  The current page
     */
    this.currentPage = 1;

    /**
     * @property {number} totalPages  The total number of pages
     */
    this.totalPages = 0;

    /**
     * @property {bool} shouldScrollIntoView  Whether to scroll the companies list into the screen
     */
    this.shouldScrollIntoView = false;

    /**
     * @property {string} searchQuery  The user search query for the compaines
     */
    this.searchQuery = '';

    /**
     * @property {string} searchCategoryId  The user search category for the categories
     */
    this.searchCategoryId = '';

    /**
     * @property {number} transitionDuration  The .companies-article CSS transition duration
     */
    this.transitionDuration = 350;

    /** Bind the events */
    this.bindEvents();

    /** Initialize */
    this.initialize();
  }

  /**
   * Bind the events
   */
  bindEvents() {
    this.onClickPrevHandler = this.onClickPrev.bind(this);
    this.onClickNextHandler = this.onClickNext.bind(this);
    this.onClickArticleHandler = this.onClickArticle.bind(this);
  }

  /**
   * Initialize
   */
  initialize() {
    /** Add the protocol to the rest url for local queries */
    if (document.querySelector('html').classList.contains('dev') && this.restUrl.indexOf('http') < 0) {
      this.restUrl = `http:${this.restUrl}`;
      this.restCategoryUrl = `http:${this.restCategoryUrl}`;
    }

    /** Get the list of companies */
    if (!document.querySelector('body').classList.contains('front-page-data')) {
      this.getCompaniesList();
    }
  }

  /**
   * Get the list of companies from the rest api, filtered by search queries and paginated
   *
   * Then populate the company list, bind the navigation and scroll to the content
   */
  getCompaniesList() {
    /** Disable the navigation */
    this.unbindNavigation();

    /** Set the rest url parameters with the search queries and the pagination */
    const restUrl = new URL(this.restUrl);
    restUrl.searchParams.append('page', String(this.currentPage));
    restUrl.searchParams.append('per_page', String(this.perPage));
    if (this.searchQuery.length > 0) {
      restUrl.searchParams.append('search', this.searchQuery);
    }
    if (this.searchCategoryId > 0) {
      restUrl.searchParams.append('entreprise_categorie', String(this.searchCategoryId));
    }

    /** Fetch the results */
    fetch(restUrl, {
      method: 'GET',
    })
      .then(responseStatus)
      .then(response => {
        this.totalPages = parseInt(response.headers.get('X-WP-TotalPages'), 10);
        return response.json();
      })
      .then(response => {
        this.populate(response);
      });
  }

  /**
   * Bind the prev / next buttons events
   */
  bindNavigation() {
    if (this.currentPage > 1) {
      this.prevButtonElt.removeAttribute('disabled');
      this.prevButtonElt.addEventListener('click', this.onClickPrevHandler);
    }
    if (this.totalPages > 1 && this.currentPage < this.totalPages) {
      this.nextButtonElt.removeAttribute('disabled');
      this.nextButtonElt.addEventListener('click', this.onClickNextHandler);
    }
  }

  /**
   * Unbind the prev / next buttons events
   */
  unbindNavigation() {
    this.prevButtonElt.setAttribute('disabled', 'true');
    this.prevButtonElt.removeEventListener('click', this.onClickPrevHandler);
    this.nextButtonElt.setAttribute('disabled', 'true');
    this.nextButtonElt.removeEventListener('click', this.onClickNextHandler);
  }

  /**
   * Navigation previous button click handler
   *
   * Load previous page
   *
   * @param {MouseEvent} e
   */
  onClickPrev(e) {
    e.currentTarget.blur();
    this.currentPage -= 1;
    this.getCompaniesList();
    this.shouldScrollIntoView = false;
  }

  /**
   * Navigation next button click handler
   *
   * Load next page
   *
   * @param {MouseEvent} e
   */
  onClickNext(e) {
    e.currentTarget.blur();
    this.currentPage += 1;
    this.getCompaniesList();
    this.shouldScrollIntoView = true;
  }

  /**
   * Scroll the content into the view
   */
  scrollToContent() {
    if (this.shouldScrollIntoView) {
      this.listElt.scrollIntoView();
      setTimeout(() => {
        window.scrollTo(0, window.scrollY - document.getElementById('head').getBoundingClientRect().height);
      }, 500);
    }
  }

  /**
   * Populate the .companies-article elements with the search results
   *
   * @param {Array} searchResults
   */
  populate(searchResults) {
    /** Dispatch a beforechange event */
    this.companiesElt.dispatchEvent(new Event('beforechange'));

    let iteration = 0;
    for (const article of this.companiesArticlesElts) {
      /** Unbind listeners and remove the content from the articles */
      const link = article.querySelector('a');
      if (link !== null) {
        link.removeEventListener('click', this.onClickArticleHandler);
      }
      article.innerHTML = '';

      /** Populate the article if there is a result matching its index or hide it otherwise */
      if (searchResults.length - 1 >= iteration) {
        /** Article content and click handler */
        const articleLink = document.createElement('a');
        articleLink.href = '#';
        articleLink.setAttribute('data-rest-url', searchResults[iteration]._links.self[0].href);
        articleLink.setAttribute('data-lat', searchResults[iteration].acf.gps_latitude);
        articleLink.setAttribute('data-lng', searchResults[iteration].acf.gps_longitude);
        articleLink.setAttribute('data-id', searchResults[iteration].id);
        articleLink.appendChild(this.renderArticleCompany(searchResults[iteration]));
        articleLink.appendChild(this.renderArticleCompanyLogo(searchResults[iteration]));
        articleLink.addEventListener('click', this.onClickArticleHandler);
        article.appendChild(articleLink);

        /** Show the article */
        this.showArticle(article);
      } else {
        /** Hide the article */
        this.hideArticle(article);
      }
      iteration += 1;
    }

    /** Expand the companies element if it is collapsed */
    if (this.companiesElt.classList.contains('is-collapsed')) {
      this.companiesElt.classList.remove('is-collapsed');
    }

    /** Bind the navigation and scroll to content */
    this.bindNavigation();
    this.scrollToContent();

    /** Dispatch a change event */
    this.companiesElt.dispatchEvent(new Event('change'));
  }

  /**
   * Render the .company element
   *
   * @param {Object} response
   * @return {HTMLElement}
   */
  renderArticleCompany(searchResult) {
    const articleCompany = document.createElement('div');
    articleCompany.className = 'company';
    const articleCompanyContent = document.createElement('div');
    articleCompany.appendChild(articleCompanyContent);
    const articleCompanyName = document.createElement('h3');
    articleCompanyName.className = 'company-name';
    articleCompanyName.innerHTML = searchResult.title.rendered;
    articleCompanyContent.appendChild(articleCompanyName);
    const articleCompanyAffiliation = document.createElement('p');
    articleCompanyAffiliation.className = 'company-affiliation';
    articleCompanyAffiliation.innerHTML = `(${this.listElt.getAttribute(
      `data-txt-${searchResult.fields.affiliation}`
    )})`;
    articleCompanyContent.appendChild(articleCompanyAffiliation);
    return articleCompany;
  }

  /**
   * Render the .company-logo element
   *
   * @param {Object} response
   * @return {HTMLElement}
   */
  renderArticleCompanyLogo(searchResult) {
    const articleCompanyLogo = document.createElement('div');
    articleCompanyLogo.className = 'company-logo';
    let logo = null;
    if (searchResult.fields.logo[0] !== undefined) {
      logo = document.createElement('img');
      logo.src = searchResult.fields.logo[0];
      logo.alt = searchResult.title.rendered;
      logo.addEventListener('load', e => (e.target.style.opacity = 1));
    } else {
      logo = document.createElement('span');
      logo.innerHTML = searchResult.title.rendered;
      setTimeout(() => (logo.style.opacity = 1), 0.5);
    }
    logo.style.opacity = 0;
    logo.style.transition = 'opacity .75s ease';
    articleCompanyLogo.appendChild(logo);
    return articleCompanyLogo;
  }

  /**
   * Article link click handler
   *
   * Load the company details from the rest api
   *
   * @param {MouseEvent} e
   */
  onClickArticle(e) {
    e.preventDefault();
    e.currentTarget.blur();
    new CompanyDetails(e.currentTarget.getAttribute('data-rest-url'), this.detailsElt);
  }

  /**
   * Show a company article with animation
   */
  showArticle(article) {
    article.removeAttribute('hidden');
    setTimeout(() => {
      article.classList.remove('is-hidden');
    }, 10);
  }

  /**
   * Hide a company article with animation
   */
  hideArticle(article) {
    article.classList.add('is-hidden');
    setTimeout(() => {
      article.setAttribute('hidden', 'true');
    }, this.transitionDuration);
  }
}



// WEBPACK FOOTER //
// ./scripts/components/CompaniesList.js